import { log } from "../core/logger";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { API_BASE_URL } from "./helpers";
import type { CreateCommentPayload, ICommentAPI } from "./commentApi";
import type { Comment, CommentFilters, CommentSort } from "@/types/Comment";
import type { Member } from "@/types/Member";
import type { PaginatedResponse } from "@/types/Pagination";

class RemoteCommentAPI implements ICommentAPI {
  clearCache(): void {
    // No caching implemented in remote API
    return;
  }

  async createComment(payload: CreateCommentPayload): Promise<Comment> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API error: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success || !result.data) {
        throw new Error("Invalid response format from server");
      }

      return result.data;
    } catch (error) {
      log("remoteCommentApi", "Failed to create comment", error);
      throw error;
    }
  }

  async fetchComments(
    filters?: CommentFilters,
    sort?: CommentSort,
    limit?: number,
    skip?: number,
  ): Promise<PaginatedResponse<Comment>> {
    try {
      // Build query parameters
      const params = new URLSearchParams();

      if (filters) {
        if (filters.entityType) params.append("entityType", filters.entityType);
        if (filters.entityId) params.append("entityId", filters.entityId);
        if (filters.authorMemberId) params.append("authorMemberId", filters.authorMemberId);
        if (filters.startDate) params.append("startDate", filters.startDate);
        if (filters.endDate) params.append("endDate", filters.endDate);
      }

      if (sort) {
        params.append("sortField", sort.field);
        params.append("sortOrder", sort.order);
      }

      if (limit !== undefined) params.append("limit", limit.toString());
      if (skip !== undefined) params.append("skip", skip.toString());

      const queryString = params.toString();
      const url = `${API_BASE_URL}/api/comments${queryString ? `?${queryString}` : ""}`;

      const response = await fetchWithAuth(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 404) {
          return {
            success: true,
            data: [],
            pagination: {
              total: 0,
              limit: limit ?? 200,
              skip: skip ?? 0,
              hasMore: false,
              returned: 0,
            },
          };
        }
        throw new Error(`API error: ${response.statusText}`);
      }

      const result: PaginatedResponse<Comment> = await response.json();
      return result;
    } catch (error) {
      log("remoteCommentApi", "Failed to fetch comments", error);
      throw error;
    }
  }

  async addCommentToEntity(
    entityType: string,
    entityId: string,
    comment: string,
    _author: Member,
  ): Promise<Comment | Error> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ entityType, entityId, content: comment }),
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const result = await response.json();
      return result.data; // Return the created comment from the response
    } catch (error) {
      log("remoteCommentApi", "Failed to add comment", error);
      return error instanceof Error ? error : new Error("Failed to add comment");
    }
  }
}

export default RemoteCommentAPI;
